import React, {memo, useMemo} from 'react'
import {KTSVG, toAbsoluteUrl} from '../../../../../../_metronic/helpers'
import {DateAno, DateMes} from '../../../../../utils/date'
import {NotImported} from '../../../../components/NotInformat'
import {EmptyInvoicesState} from '../EmptyInvoicesState'
import styles from './boardInvoices.module.css'

type Props = {
  invoices: any[]
  setMyInvoice: (invoice: any) => void
  setShowCreateAppModal: (show: boolean) => void
  setShowCreateAppModalDelete: (show: boolean) => void
}

type UnitGroup = {
  unitId: string
  unitName: string
  contrato: string
  codigoInstalacao: string
  type: string
  status: string
  concessionaire: string
  concessionaireImage: string
  invoices: any[]
}

type PlantGroup = {
  plantId: string
  plantName: string
  units: UnitGroup[]
}

const getNextDaysIso = (dateString: string, days: number) => {
  const date = new Date(dateString)
  if (isNaN(date.getTime())) return null
  date.setDate(date.getDate() + days)
  return date.toISOString()
}

const typeBadgeColor = (type?: string) => {
  if (type === 'GERADORA') return 'primary'
  if (type === 'CONSUMIDORA') return 'info'
  return 'secondary'
}

const statusBadgeColor = (status?: string) => {
  if (status === 'INFORMADO') return 'success'
  if (status === 'NÃO INFORMADO') return 'danger'
  return 'secondary'
}

const DEFAULT_CONCESSIONAIRE_IMAGE =
  'https://res.cloudinary.com/deq3ehxe3/image/upload/v1771533247/1275576_zn46s3.png'

const getConcessionaireImageSrc = (linkImagem?: string | null) => {
  if (linkImagem) {
    return linkImagem.startsWith('http') ? linkImagem : toAbsoluteUrl(linkImagem)
  }

  return toAbsoluteUrl(DEFAULT_CONCESSIONAIRE_IMAGE)
}

export const groupInvoicesByPlant = (invoices: any[]): PlantGroup[] => {
  const plantsMap = new Map<string, {plantId: string; plantName: string; unitsMap: Map<string, UnitGroup>}>()

  invoices.forEach((invoice) => {
    const plant = invoice.unitConsumer?.plant
    const plantId = plant?.id || 'sem-usina'
    const plantName = plant?.name || 'Sem usina'

    if (!plantsMap.has(plantId)) {
      plantsMap.set(plantId, {plantId, plantName, unitsMap: new Map()})
    }

    const plantGroup = plantsMap.get(plantId)!
    const unit = invoice.unitConsumer
    const unitId = unit?.id || 'sem-unidade'

    if (!plantGroup.unitsMap.has(unitId)) {
      plantGroup.unitsMap.set(unitId, {
        unitId,
        unitName: unit?.name || 'Sem unidade',
        contrato: unit?.contrato || '',
        codigoInstalacao: invoice?.codigo_instalacao || '',
        type: unit?.type_unit_consumer || '',
        status: unit?.status || '',
        concessionaire: unit?.concessioraire?.name || '',
        concessionaireImage: unit?.concessioraire?.link_imagem || '',
        invoices: [],
      })
    }

    plantGroup.unitsMap.get(unitId)!.invoices.push(invoice)
    if (!plantGroup.unitsMap.get(unitId)!.codigoInstalacao && invoice?.codigo_instalacao) {
      plantGroup.unitsMap.get(unitId)!.codigoInstalacao = invoice.codigo_instalacao
    }
  })

  return Array.from(plantsMap.values())
    .map((plant) => ({
      plantId: plant.plantId,
      plantName: plant.plantName,
      units: Array.from(plant.unitsMap.values())
        .map((unit) => ({
          ...unit,
          invoices: [...unit.invoices].sort((a, b) => {
            const dateA = a.mes ? new Date(a.mes).getTime() : 0
            const dateB = b.mes ? new Date(b.mes).getTime() : 0
            return dateB - dateA
          }),
        }))
        .sort((a, b) => {
          if (a.type !== b.type) {
            if (a.type === 'GERADORA') return -1
            if (b.type === 'GERADORA') return 1
          }
          return a.unitName.localeCompare(b.unitName, 'pt-BR')
        }),
    }))
    .sort((a, b) => {
      if (a.plantId === 'sem-usina') return 1
      if (b.plantId === 'sem-usina') return -1
      return a.plantName.localeCompare(b.plantName, 'pt-BR')
    })
}

export const BoardInvoices = memo(function BoardInvoices({
  invoices,
  setMyInvoice,
  setShowCreateAppModal,
  setShowCreateAppModalDelete,
}: Props) {
  const plantGroups = useMemo(() => groupInvoicesByPlant(invoices || []), [invoices])

  const handleOpenInvoice = (invoice: any) => {
    const updatedInvoice = {
      ...invoice,
      idUnitConsumer: invoice.unitConsumer?.id || null,
      idConcessionarie: invoice.unitConsumer?.concessioraire?.id || null,
    }

    setMyInvoice(updatedInvoice)
    setShowCreateAppModal(true)
  }

  const handleDeleteInvoice = (event: React.MouseEvent, invoice: any) => {
    event.preventDefault()
    event.stopPropagation()
    setMyInvoice(invoice)
    setShowCreateAppModalDelete(true)
  }

  if (!invoices || invoices.length === 0) {
    return <EmptyInvoicesState />
  }

  return (
    <div className={styles.boardGrid}>
      {plantGroups.map((plant) => {
        const invoicesCount = plant.units.reduce((acc, unit) => acc + unit.invoices.length, 0)
        const informedCount = plant.units.reduce(
          (acc, unit) => acc + unit.invoices.filter((invoice) => invoice.status === 'INFORMADO').length,
          0
        )

        return (
          <article key={plant.plantId} className={styles.plantCard}>
            <header className={styles.plantHeader}>
              <img
                src={toAbsoluteUrl('/media/imgs/plant2.png')}
                alt=''
                className={styles.plantIcon}
              />
              <div className={styles.plantTitle}>
                <span className={styles.plantName} title={plant.plantName}>
                  {plant.plantName}
                </span>
                <div className={styles.plantMeta}>
                  <span className='badge badge-light fs-8'>
                    {plant.units.length} {plant.units.length === 1 ? 'unidade' : 'unidades'}
                  </span>
                  <span className='badge badge-light fs-8'>
                    {invoicesCount} {invoicesCount === 1 ? 'fatura' : 'faturas'}
                  </span>
                  <span className='badge badge-light-success fs-8'>{informedCount} informada(s)</span>
                </div>
              </div>
            </header>

            <div className={styles.unitsWrap}>
              {plant.units.map((unit) => (
                <section key={unit.unitId} className={styles.unitBlock}>
                  <div className={styles.unitHeader}>
                    <div className={styles.unitTitleRow}>
                      <KTSVG
                        path='/media/icons/duotune/general/gen001.svg'
                        className={`svg-icon-3 svg-icon-muted ${styles.unitIcon}`}
                      />
                      <div className={styles.unitName}>{unit.unitName}</div>
                      <span
                        className={`badge badge-light-${typeBadgeColor(unit.type)} fs-8 fw-semibold ${styles.unitTypeBadge}`}
                      >
                        {unit.type || <NotImported className='fs-8' />}
                      </span>
                    </div>
                  </div>

                  {(unit.concessionaire || unit.codigoInstalacao || unit.contrato) && (
                    <div className={styles.unitSubRow}>
                      <div className={styles.concessionaireInfo}>
                        {unit.concessionaire ? (
                          <>
                            <img
                              src={getConcessionaireImageSrc(unit.concessionaireImage)}
                              alt={unit.concessionaire}
                              title={unit.concessionaire}
                              className={styles.concessionaireImage}
                            />
                            <span className={styles.concessionaireName}>{unit.concessionaire}</span>
                          </>
                        ) : null}
                      </div>
                      <div className={styles.unitSide}>
                        {unit.contrato ? (
                          <span className={styles.unitMetaValue} title='Número da unidade'>
                            Nº {unit.contrato}
                          </span>
                        ) : null}
                        {unit.codigoInstalacao ? (
                          <span className={styles.unitMetaLabel} title='Código de instalação'>
                            Inst. {unit.codigoInstalacao}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  )}

                  <div className={styles.invoicesList}>
                    {unit.invoices.map((invoice) => (
                      <div
                        key={invoice.id}
                        className={styles.invoiceItem + ' bg-body'}
                        onClick={() => handleOpenInvoice(invoice)}
                      >
                        <div className={styles.invoiceInfo}>
                          <KTSVG
                            path='/media/icons/duotune/ecommerce/ecm010.svg'
                            className='svg-icon-2 svg-icon-muted'
                          />
                          <span className={styles.invoiceMonth}>
                            {invoice.mes ? (
                              `${DateMes(getNextDaysIso(invoice.mes, 3))} ${DateAno(invoice.mes)}`
                            ) : (
                              <NotImported className='fs-8' />
                            )}
                          </span>
                        </div>

                        <div className={styles.invoiceActions}>
                          <span
                            className={`badge badge-light-${statusBadgeColor(invoice.status)} fs-8 fw-semibold`}
                          >
                            {invoice.status || '—'}
                          </span>
                          <span
                            className={`btn btn-icon btn-sm ${
                              invoice.confirm_payment ? 'btn-bg-success' : 'btn-bg-danger'
                            } ${styles.paymentIcon}`}
                            style={{cursor: 'default', width: '1.6rem', height: '1.6rem'}}
                            data-tooltip={invoice.confirm_payment ? 'Pago' : 'Não pago'}
                          >
                            <KTSVG
                              path={
                                invoice.confirm_payment
                                  ? '/media/icons/duotune/arrows/arr085.svg'
                                  : '/media/icons/duotune/arrows/arr097.svg'
                              }
                              className='svg-icon-5 svg-icon-light'
                            />
                          </span>
                          {invoice.unitConsumer?.type_unit_consumer !== 'GERADORA' && (
                            <button
                              type='button'
                              className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                              style={{width: '1.6rem', height: '1.6rem'}}
                              title='Apagar fatura'
                              onClick={(event) => handleDeleteInvoice(event, invoice)}
                            >
                              <KTSVG
                                path='/media/icons/duotune/general/gen027.svg'
                                className='svg-icon-5'
                              />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </article>
        )
      })}
    </div>
  )
})
